/**
 * הרשמה עצמית — the public door, and the two locks on it. §10ב, migration 0032.
 *
 * ── WHY THIS SCREEN EXISTS ──────────────────────────────────────────────────────────────────────
 *
 * §10ב reported that self-registration shipped CLOSED and that opening it meant running two UPDATE
 * statements by hand — one on `billing.commercial_policy`, one per plan on `billing.plan`. The
 * Owner should not have to open a database to decide whether strangers may buy this product.
 *
 * ── THE TWO LOCKS ARE SEPARATE, AND THE SCREEN SAYS SO ──────────────────────────────────────────
 *
 * `self_signup_open` decides whether the door opens at all. `billing.plan.is_public_signup` decides
 * which plans a stranger may buy once it does. **Neither is sufficient alone**: an open door with
 * no marked plan offers nothing, and a marked plan behind a shut door sells nothing — so this
 * screen renders the combination as one sentence rather than leaving the reader to work it out
 * from two controls.
 *
 * ── AND IT SHOWS THE RAW VALUE WHEN IT IS NOT THE CANONICAL WORD ────────────────────────────────
 *
 * The server opens the door on the exact word `true` (case and surrounding space forgiven) and
 * treats everything else as shut. Somebody who typed `yes` has a shut door; a screen that reported
 * only "closed" would leave them certain they had opened it. So when the stored value is neither
 * `true` nor `false`, the word itself is shown.
 */

import { useCallback, useState } from 'react';
import type { FormEvent } from 'react';

import { AppShell } from '../shell/AppShell';
import { useWorkContext } from '../shell/ContextProvider';
import { ServerRead } from '../components/ServerRead';
import { SurfaceNotice } from '../components/SurfaceNotice';
import { ActionFailureNotice } from '../components/ActionFailureNotice';
import type { ActionFailure } from '../api/actionOutcome';
import { classifyActionError } from '../api/actionOutcome';
import { callOperation } from '../api/operations';
import { useServerData } from '../api/useServerData';
import { formatInstantHe } from '../api/adminVocabulary';
import { routes } from '../routes';

const TITLE_HE = 'הרשמה עצמית';

/** The two shapes the door read can return. Mirrors `SignupDoorView` on the server. */
interface DoorRead {
  readonly door:
    | {
        readonly declared: true;
        readonly open: boolean;
        readonly rawValue: string;
        readonly updatedAtUtc: string;
        readonly reason: string;
      }
    | { readonly declared: false; readonly open: false };
}

export function PlatformSignupDoorScreen(): JSX.Element {
  const ctx = useWorkContext();

  const readDoor = useCallback(() => callOperation('platform.signup_door.read', {}), []);
  const readPlans = useCallback(() => callOperation('platform.catalogue.plan.read', {}), []);
  const door = useServerData(readDoor);
  const plans = useServerData(readPlans);

  const [failure, setFailure] = useState<ActionFailure | null>(null);
  const [sending, setSending] = useState(false);

  if (ctx.surface !== 'PLATFORM') {
    return <SurfaceNotice titleHe={TITLE_HE} requires="PLATFORM" actual={ctx.surface} />;
  }

  function send(work: Promise<unknown>, reload: () => void): void {
    setSending(true);
    setFailure(null);
    void work
      .then(
        () => reload(),
        (err: unknown) => setFailure(classifyActionError(err)),
      )
      .finally(() => setSending(false));
  }

  function setDoor(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    /*
     * ══ THE SUBMITTER, AND `new FormData(form)` DOES NOT CARRY IT. ═══════════════════════════
     *
     * The first draft read `form.get('intent')` after `new FormData(e.target)`. **That is wrong in
     * every browser, not only under test**: the FormData constructor collects the form's CONTROLS,
     * and a submit button's name/value enter a submission only through the submitter — either as
     * `new FormData(form, submitter)` or from `SubmitEvent.submitter`. So the value was always
     * absent, `=== 'open'` was always false, and pressing "פתיחת הדלת" would have SHUT the door.
     *
     * `signupDoor.dom.test.tsx` caught it by asserting the body rather than the rendering, which is
     * the only way this class of defect is visible: the screen looked correct and did the opposite.
     *
     * Two submit buttons rather than a checkbox is kept on purpose — the person chooses "open" or
     * "close", instead of toggling a box whose current state they then have to read back.
     */
    const form = e.currentTarget;
    const submitter = (e.nativeEvent as SubmitEvent).submitter as HTMLButtonElement | null;
    const open = submitter?.value === 'open';
    send(
      callOperation('platform.signup_door.set', {
        body: { open, reason: String(new FormData(form).get('reason') ?? '') },
      }),
      door.reload,
    );
  }

  function setPlan(planCode: string, open: boolean): void {
    send(
      callOperation('platform.catalogue.plan.public_signup.set', { body: { planCode, open } }),
      plans.reload,
    );
  }

  return (
    <AppShell
      titleHe={TITLE_HE}
      backToParent={{ labelHe: 'קטלוג המוצר', href: routes.platformCatalogue }}
    >
      {failure === null ? null : <ActionFailureNotice failure={failure} />}

      <ServerRead result={door.result} whatHe="את מצב ההרשמה העצמית" skeletonRows={2}>
        {(data: DoorRead) => (
          <>
            <section className="figures" aria-label="מצב הדלת">
              <article className="figure">
                <p className="figure__label">דלת ההרשמה</p>
                <p className="figure__value">{data.door.open ? 'פתוחה' : 'סגורה'}</p>
                <p className="figure__note">
                  {data.door.declared
                    ? data.door.open
                      ? 'זר יכול לפתוח ארגון בעצמו — אם יש תכנית מסומנת למטה'
                      : 'אף אחד אינו יכול להירשם לבד, וכל מסכי ההרשמה מחזירים 404'
                    : 'שורת המדיניות אינה קיימת בשרת הזה, ולכן הדלת סגורה'}
                </p>
              </article>
            </section>

            {/* THE WORD THAT IS ACTUALLY IN THE ROW, when it is not one of the two the server
                writes. `yes` leaves the door shut, and seeing "סגורה" alone would not explain why. */}
            {data.door.declared && data.door.rawValue !== 'true' && data.door.rawValue !== 'false' ? (
              <section className="breakdown" aria-label="הערך השמור">
                <h3 className="breakdown__title">הערך השמור אינו אחת משתי המילים</h3>
                <p className="tile__lead">
                  בשורה כתוב <span dir="ltr">{data.door.rawValue}</span> — והשרת פותח את הדלת רק על
                  המילה <span dir="ltr">true</span>. כל ערך אחר נחשב סגור.
                </p>
              </section>
            ) : null}

            {data.door.declared ? (
              <section className="breakdown" aria-label="השינוי האחרון">
                <h3 className="breakdown__title">השינוי האחרון</h3>
                <dl className="not-available__list">
                  <div className="not-available__item">
                    <dt>מתי</dt>
                    <dd dir="ltr">{formatInstantHe(data.door.updatedAtUtc)}</dd>
                  </div>
                  <div className="not-available__item">
                    <dt>מדוע</dt>
                    <dd>{data.door.reason}</dd>
                  </div>
                </dl>
              </section>
            ) : null}
          </>
        )}
      </ServerRead>

      <section className="actions-row">
        <form className="signin__form" onSubmit={setDoor}>
          <h3 className="breakdown__title">פתיחה או סגירה של הדלת</h3>
          <p className="tile__lead">
            פתיחת הדלת לבדה אינה מאפשרת דבר: צריך גם תכנית אחת לפחות מסומנת למטה. סגירה מחזירה את
            כל מסכי ההרשמה ל-404 מיד, ואינה פוגעת בארגון שכבר נפתח.
          </p>
          <label className="field">
            {/* REQUIRED, and the database agrees: `CK_commercial_policy_reason`. This is the row an
                auditor asks about first — it decides whether strangers may create tenants. */}
            <span className="field__label">סיבת השינוי</span>
            <input className="field__input" name="reason" minLength={3} required />
          </label>
          <div className="actions-row">
            <button
              type="submit"
              name="intent"
              value="open"
              className="btn btn--primary"
              disabled={sending}
            >
              פתיחת הדלת
            </button>
            <button type="submit" name="intent" value="close" className="btn" disabled={sending}>
              סגירת הדלת
            </button>
          </div>
        </form>
      </section>

      <ServerRead result={plans.result} whatHe="את תכניות הקטלוג" skeletonRows={3}>
        {/* NO LOCAL TYPE for the plan list: `operations.ts` declares the response shape and a
            second, narrower interface here would be a second answer about the same rows. */}
        {(data) => (
          <section className="breakdown" aria-label="תכניות שזר יכול לרכוש">
            <h3 className="breakdown__title">אילו תכניות זר יכול לרכוש</h3>
            <p className="tile__lead">
              תכנית שאינה מסומנת כאן אינה מוצגת בדלת הציבורית כלל. זו ההחלטה המסחרית השנייה, והיא
              נפרדת מפתיחת הדלת בכוונה.
            </p>
            <dl className="not-available__list">
              {data.plans.map((plan) => (
                <div className="not-available__item" key={plan.planCode}>
                  <dt>
                    {plan.displayNameHe} <span dir="ltr">({plan.planCode})</span>
                  </dt>
                  <dd>
                    {plan.publicSignup ? 'מסומנת לרכישה עצמית' : 'אינה לרכישה עצמית'}
                    <button
                      type="button"
                      className="btn btn--nav"
                      disabled={sending}
                      onClick={() => setPlan(plan.planCode, !plan.publicSignup)}
                    >
                      {plan.publicSignup ? 'הסרת הסימון' : 'סימון לרכישה עצמית'}
                    </button>
                  </dd>
                </div>
              ))}
            </dl>
          </section>
        )}
      </ServerRead>
    </AppShell>
  );
}
